import { CommonModule } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  Output,
  signal,
  SimpleChanges,
  ViewChild,
} from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { FormBuilder, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatCardModule } from '@angular/material/card'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatIconModule } from '@angular/material/icon'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatRadioModule } from '@angular/material/radio'
import { MatSelectModule } from '@angular/material/select'
import { MatSlideToggleModule } from '@angular/material/slide-toggle'
import { MatTabsModule } from '@angular/material/tabs'
import { MatTooltipModule } from '@angular/material/tooltip'
import { PipesModule } from '@mmx/pipes'
import {
  AddressSchema,
  Card,
  Clinic,
  ConfirmService,
  CustomFieldService,
  emailValidator,
  EstimateBalance,
  Patient,
  PAYMENT_PROVIDER,
  PaymentService,
  TilledBillingDetail,
  TransactionMethod,
} from '@mmx/shared'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { StripeElementLocale } from '@stripe/stripe-js'
import { from, merge, of, throwError } from 'rxjs'
import { catchError, map, switchMap, take } from 'rxjs/operators'
import { v4 as uuidv4 } from 'uuid'

import { environment } from '../../../../../../environments/environment'
import { AddressFieldModule } from '../../../address-field'
import { CustomFieldsFormComponent } from '../../../custom-fields-form'
import {
  PaymentCardFormComponent,
  PaymentCardFormModule,
  PaymentProviderConfiguration,
} from '../../../payment-card-form'
import { balanceOutstanding } from '../../balance-utils'
import { ApplyBalancePaymentParams, EstimateBalanceApiService } from '../../services/estimate-balance-api.service'
import { BalancePaymentPlanPanelComponent } from '../balance-payment-plan-panel/balance-payment-plan-panel.component'
import { NEW_CARD_SENTINEL, SavedCardSelectComponent } from '../saved-card-select/saved-card-select.component'

export { NEW_CARD_SENTINEL }

type CollectMethod = 'cash' | 'creditCard' | 'check'

function roundCents(n: number): number {
  return Math.round(n * 100) / 100
}

export interface CollectPaymentResult {
  balance: EstimateBalance
  applied: boolean
  reason?: 'replay'
}

@Component({
  selector: 'mmx-balance-collect-payment-card',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatCardModule,
    MatCheckboxModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressBarModule,
    MatRadioModule,
    MatSelectModule,
    MatSlideToggleModule,
    MatTabsModule,
    MatTooltipModule,
    PipesModule,
    TranslateModule,
    AddressFieldModule,
    CustomFieldsFormComponent,
    PaymentCardFormModule,
    BalancePaymentPlanPanelComponent,
    SavedCardSelectComponent,
  ],
  templateUrl: './balance-collect-payment-card.component.html',
  styleUrls: ['./balance-collect-payment-card.component.scss'],
})
export class BalanceCollectPaymentCardComponent implements OnChanges {
  @Input() balance: EstimateBalance | null = null
  @Input() clinic: Clinic | null = null
  @Input() patient: Patient | null = null
  /** Container-level busy flag — disables Submit so a Save/Calculate can't race a payment. */
  @Input() busy = false

  @Output() readonly paymentApplied = new EventEmitter<CollectPaymentResult>()
  @Output() readonly paymentPlanCreated = new EventEmitter<EstimateBalance>()
  @Output() readonly paymentPlanCanceled = new EventEmitter<EstimateBalance>()

  // Mirrors the picker's last emission so getters like `showCreditCardOption` can
  // make sync decisions without reaching through the @ViewChild reference.
  readonly savedCards = signal<Card[]>([])
  readonly submitting = signal<boolean>(false)
  readonly error = signal<string | null>(null)

  /** True once the card is captured but the payment could not be recorded; blocks a second capture. */
  readonly chargeCaptured = signal<boolean>(false)

  /** Derives the Stripe idempotency key so a retried submit reuses one PaymentIntent. */
  private attemptToken: string = uuidv4()

  @ViewChild(PaymentCardFormComponent) paymentCardForm?: PaymentCardFormComponent
  // The plan panel lives inside this card's template, not the parent estimate-
  // balance component. ViewChild only reaches the host's own template, so the
  // parent must delegate refresh through us — see refresh() below.
  @ViewChild(BalancePaymentPlanPanelComponent) paymentPlanPanel?: BalancePaymentPlanPanelComponent
  @ViewChild(SavedCardSelectComponent) savedCardSelect?: SavedCardSelectComponent

  readonly form = this.fb.group({
    paymentMethod: this.fb.control<CollectMethod>('cash', Validators.required),
    amount: this.fb.control<number>(0, [Validators.required, Validators.min(0.01)]),
    paymentMethodId: this.fb.control<string>(''),
    holderName: this.fb.control<string>(''),
    // Billing email for Tilled new-card flow; also used as patientEmail on the
    // backend when the patient doesn't have one on file. Autofilled from patient.email.
    // emailValidator is attached only while the add-new-card form is open - see syncEmailValidator.
    email: this.fb.control<string>(''),
    // Billing address — Tilled-only requirement. AddressSchema covers all the
    // optional address pieces; the cross-field validator enforces country + zip
    // when adding a new card via Tilled.
    holderAddress: this.fb.control<AddressSchema | null>(null),
    checkNumber: this.fb.control<string>(''),
    notes: this.fb.control<string>(''),
    areNotesVisibleToPatient: this.fb.control<boolean>(false),
    applyDiscount: this.fb.control<boolean>(false),
    customFields: this.fb.control<Record<string, unknown> | null>(null),
  }, {
    validators: this.crossFieldValidator.bind(this),
  })

  readonly transactionFields$ = this.customFieldService.transactionFields$

  private readonly destroyRef = inject(DestroyRef)

  private readonly confirmService = inject(ConfirmService)

  private readonly translateService = inject(TranslateService)

  constructor(
    private readonly fb: FormBuilder,
    private readonly api: EstimateBalanceApiService,
    private readonly customFieldService: CustomFieldService,
    private readonly paymentService: PaymentService,
  ) {
    merge(
      this.form.controls.paymentMethod.valueChanges,
      this.form.controls.paymentMethodId.valueChanges,
    )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.syncEmailValidator())
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['balance'] && this.balance) {
      this.form.controls.amount.setValue(balanceOutstanding(this.balance), { emitEvent: false })
    }
    if (changes['patient']) {
      this.autofillFromPatient()
    }
  }

  /**
   * The email input only renders inside the add-new-card form, so validating it anywhere else
   * would let a hidden invalid value disable Charge with nothing on screen to correct - and a
   * cash or check payment has already changed hands, so it must never be blocked by this field.
   */
  private syncEmailValidator(): void {
    const email = this.form.controls.email
    if (this.isAddingNewCard === email.hasValidator(emailValidator)) {
      return
    }
    if (this.isAddingNewCard) {
      email.addValidators(emailValidator)
    } else {
      email.removeValidators(emailValidator)
    }
    email.updateValueAndValidity({ emitEvent: false })
  }

  /**
   * Pre-fill billing details from the patient record on each new patient binding.
   * Only overwrites a still-pristine value so an operator's manual edit survives a
   * re-render. Matches legacy charge-card's ngOnInit autofill.
   */
  private autofillFromPatient(): void {
    const patient = this.patient
    if (!patient) {
      return
    }
    // standardName is a class getter that reads patient.name — guard against test
    // fixtures (and any real record) that arrive without a name attached.
    const fullName = patient.name ? patient.standardName : ''
    if (fullName && this.form.controls.holderName.pristine) {
      this.form.controls.holderName.setValue(fullName.toUpperCase(), { emitEvent: false })
    }
    if (patient.email && this.form.controls.email.pristine) {
      this.form.controls.email.setValue(patient.email, { emitEvent: false })
    }
  }

  /** Tilled variant additionally requires billing email + holder name. */
  get isTilledProvider(): boolean {
    return this.clinic?.paymentProvider?.selectedProvider === PAYMENT_PROVIDER.TILLED
  }

  get chargeAmount(): number {
    const raw = this.form.controls.amount.value
    return typeof raw === 'number' && raw > 0 ? raw : 0
  }

  /** Patient owes this much after prior payments/refunds; never negative. */
  get outstanding(): number {
    return this.balance ? balanceOutstanding(this.balance) : 0
  }

  /** Dollars over the outstanding amount that this charge would push past. > 0 only when overcharging. */
  get overchargeAmount(): number {
    const over = this.chargeAmount - this.outstanding
    return over > 0 ? roundCents(over) : 0
  }

  get isOvercharging(): boolean {
    return this.overchargeAmount > 0
  }

  /**
   * Persisted overcharge on the balance itself: paid minus refunded exceeds the
   * patient's responsibility, so prior collections already pushed past the bill.
   * Independent of the form input — the row that surfaces it is informational,
   * not a prediction of what the in-progress submit will do.
   */
  get balanceOvercharge(): number {
    if (!this.balance) {
      return 0
    }
    const netPaid = (this.balance.paidAmount ?? 0) - (this.balance.refundedAmount ?? 0)
    const owed = this.balance.patientResponsibility ?? 0
    const over = netPaid - owed
    return over > 0 ? roundCents(over) : 0
  }

  get isBalanceOvercharged(): boolean {
    return this.balanceOvercharge > 0
  }

  get hasPriorPayments(): boolean {
    return (this.balance?.paidAmount ?? 0) > 0
  }

  /** True when the balance already carries an active Stripe Subscription Schedule. */
  get hasActivePaymentPlan(): boolean {
    return this.balance?.paymentPlan != null
  }

  /**
   * Whether the clinic can accept a brand-new card. Both Stripe and Tilled are
   * supported — the embedded payment-card-form switches its internal renderer
   * based on the provider. We don't pre-empt onboarding (incomplete Stripe
   * accounts surface their own error inside the form); otherwise dev/test
   * clinics with no provider configured would see only cash and can't exercise
   * the card path.
   */
  get allowNewCard(): boolean {
    return this.clinic != null
  }

  get showCreditCardOption(): boolean {
    return this.savedCards().length > 0 || this.allowNewCard
  }

  get isAddingNewCard(): boolean {
    return this.form.controls.paymentMethod.value === 'creditCard'
      && this.form.controls.paymentMethodId.value === NEW_CARD_SENTINEL
  }

  /** Parity with legacy charge-card: gated by the clinic's prompt-payment preference. */
  get showDiscount(): boolean {
    return this.clinic?.modulesPreferences?.promptPayments === true
  }

  get discountPercent(): number {
    return this.clinic?.modulesPreferences?.promptPaymentDiscount ?? 0
  }

  get providerConfiguration(): PaymentProviderConfiguration {
    const provider = this.clinic?.paymentProvider
    return {
      provider: provider?.selectedProvider ?? PAYMENT_PROVIDER.STRIPE,
      locale: 'en' as StripeElementLocale,
      stripeAccount: provider?.id ?? '',
      stripePublicKey: environment.stripePublicKey,
      tilledId: provider?.tilledId ?? '',
      tilledPublicKey: environment.tilledPublicKey,
      tilledProductionMode: environment.tilledProductionMode,
    }
  }

  canSubmit(): boolean {
    if (this.hasActivePaymentPlan || this.chargeCaptured()) {
      return false
    }
    return this.form.valid && !this.submitting() && !this.busy
  }

  onCardFormUpdated(): void {
    this.form.updateValueAndValidity()
  }

  onPaymentPlanCreated(balance: EstimateBalance): void {
    this.paymentPlanCreated.emit(balance)
  }

  onPaymentPlanCanceled(balance: EstimateBalance): void {
    this.paymentPlanCanceled.emit(balance)
  }

  /**
   * Re-fetch saved cards AND cascade refresh into the embedded payment-plan
   * panel. The parent estimate-balance component calls this after a balance
   * mutation (payment applied, plan created/canceled). Without the cascade
   * the plan panel would render stale data until the next route load — the
   * panel sits inside *this* template, so only this component can reach it.
   */
  refresh(): void {
    this.savedCardSelect?.refresh()
    this.paymentPlanPanel?.refresh()
  }

  /**
   * Side-effect handler for the picker's cardsLoaded event. Mirrors the legacy
   * fetchSavedCards subscribe: on a pristine paymentMethod radio, switch to
   * 'creditCard' and pre-select the first saved card (no auto-pick of the
   * "new card" sentinel — that would eagerly render the Stripe/Tilled iframe).
   */
  onSavedCardsLoaded(list: Card[]): void {
    this.savedCards.set(list)
    if (this.form.controls.paymentMethod.pristine && this.allowNewCard) {
      this.form.controls.paymentMethod.setValue('creditCard', { emitEvent: false })
      if (list.length > 0) {
        this.form.controls.paymentMethodId.setValue(list[0].id, { emitEvent: false })
      }
    }
  }

  submit(): void {
    if (!this.canSubmit() || !this.balance || !this.clinic) {
      return
    }

    // Overcharge guard: if the amount exceeds outstanding (or prior payments
    // already exist), require explicit operator confirmation before billing.
    // ConfirmService returns Observable<boolean>; bail when the user declines.
    if (this.isOvercharging) {
      this.confirmService
        .confirm({
          message: 'balance.payment.overcharge-confirm',
          translationTokens: {
            amount: this.formatUsd(this.overchargeAmount),
            charge: this.formatUsd(this.chargeAmount),
            outstanding: this.formatUsd(this.outstanding),
          },
        })
        .pipe(take(1))
        .subscribe((confirmed) => {
          if (!confirmed) {
            return
          }
          this.submitConfirmed()
        })
      return
    }

    this.submitConfirmed()
  }

  private submitConfirmed(): void {
    if (!this.balance || !this.clinic) {
      return
    }
    const value = this.form.getRawValue()

    if (value.paymentMethod === 'creditCard' && value.paymentMethodId === NEW_CARD_SENTINEL) {
      if (!this.paymentCardForm?.valid) {
        return
      }
      this.submitNewCard(value)
      return
    }

    const method = this.resolveTransactionMethod(value.paymentMethod)

    const provider = this.clinic.paymentProvider?.selectedProvider ?? PAYMENT_PROVIDER.STRIPE

    const params: ApplyBalancePaymentParams = {
      method,
      amount: value.amount!,
      provider,
      notes: value.notes || undefined,
      areNotesVisibleToPatient: value.areNotesVisibleToPatient ?? false,
      paymentMethodId: value.paymentMethod === 'creditCard' ? value.paymentMethodId! : undefined,
      paymentMethodTypes: value.paymentMethod === 'creditCard' ? ['card'] : undefined,
      checkNumber: value.paymentMethod === 'check' ? value.checkNumber || undefined : undefined,
      customFields: this.normalizeCustomFields(value.customFields),
      patientEmail: value.email || undefined,
    }

    this.error.set(null)
    this.submitting.set(true)

    this.api.applyPayment(this.balance.id, params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.submitting.set(false)
          this.handleSuccess({ balance: result.balance, applied: result.applied, reason: result.reason })
        },
        error: (err: unknown) => {
          this.submitting.set(false)
          this.error.set(this.extractErrorMessage(err))
        },
      })
  }

  private resolveTransactionMethod(method: CollectMethod): TransactionMethod {
    switch (method) {
      case 'creditCard': return TransactionMethod.STAFF_CREDIT_CARD
      case 'check': return TransactionMethod.CHECK
      case 'cash':
      default: return TransactionMethod.CASH
    }
  }

  private submitNewCard(value: ReturnType<typeof this.form.getRawValue>): void {
    if (!this.balance || !this.clinic) {
      return
    }
    const cardForm = this.paymentCardForm!
    const balanceId = this.balance.id

    this.error.set(null)
    this.submitting.set(true)

    if (this.isTilledProvider) {
      this.submitNewTilledCard(value, cardForm, balanceId)
    } else {
      this.submitNewStripeCard(value, cardForm, balanceId)
    }
  }

  private submitNewStripeCard(
    value: ReturnType<typeof this.form.getRawValue>,
    cardForm: PaymentCardFormComponent,
    balanceId: string,
  ): void {
    const billingDetails = { name: value.holderName ?? '' }

    this.api.createPaymentIntent(balanceId, {
      amount: value.amount!,
      saveCard: cardForm.shouldSaveCard,
      attemptToken: this.attemptToken,
    })
      .pipe(
        switchMap((intent) => from(cardForm.confirmStripePayment(intent as never, billingDetails))),
        switchMap((result: { error?: { message?: string }, paymentIntent?: { id: string } }) => {
          if (result.error) {
            return throwError(() => result.error)
          }
          if (!result.paymentIntent) {
            return throwError(() => new Error('Stripe confirm returned no PaymentIntent'))
          }

          // Past this point the card is captured, so a later failure is a bookkeeping failure.
          this.chargeCaptured.set(true)

          return this.api.applyPayment(balanceId, {
            method: TransactionMethod.STAFF_CREDIT_CARD,
            amount: value.amount!,
            provider: PAYMENT_PROVIDER.STRIPE,
            paymentIntentId: result.paymentIntent.id,
            paymentMethodTypes: ['card'],
            holderName: value.holderName || undefined,
            notes: value.notes || undefined,
            areNotesVisibleToPatient: value.areNotesVisibleToPatient ?? false,
            customFields: this.normalizeCustomFields(value.customFields),
            patientEmail: value.email || undefined,
          })
        }),
        catchError((err) => {
          this.submitting.set(false)
          this.error.set(this.chargeCaptured()
            ? this.translateService.instant('transactions.charged-receipt-failed')
            : this.extractErrorMessage(err))
          return of(null)
        }),
        // takeUntilDestroyed cancels the entire chain when the host is torn down
        // mid-Stripe-confirm. Without this, a slow Stripe.js Promise outliving
        // the component would still call applyPayment and write an orphan transaction.
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((applyResult) => {
        if (applyResult == null) {
          return
        }
        this.submitting.set(false)
        this.chargeCaptured.set(false)
        this.attemptToken = uuidv4()
        this.handleSuccess({
          balance: applyResult.balance,
          applied: applyResult.applied,
          reason: applyResult.reason,
        })
      })
  }

  /**
   * Tilled new-card path. Two steps:
   *   1. createTilledPaymentMethod() against the embedded form, yielding a `pm_*` id.
   *   2. If the operator chose "save card" AND we have a patient, attach the method
   *      to the customer record so future flows can re-use it.
   * Then forward to applyPayment with the resolved id as `paymentMethodId`. Unlike
   * Stripe there's no payment intent — Tilled charges against the attached/transient
   * method id directly on the backend.
   */
  private submitNewTilledCard(
    value: ReturnType<typeof this.form.getRawValue>,
    cardForm: PaymentCardFormComponent,
    balanceId: string,
  ): void {
    const address = value.holderAddress
    const billing: TilledBillingDetail = {
      name: value.holderName ?? '',
      email: value.email ?? '',
      address: {
        country: address?.country ?? '',
        zip: address?.zip ?? '',
        state: address?.state,
        city: address?.city,
        street: address?.address1,
      },
    }
    const patientId = this.patient?.id ?? null
    const shouldAttach = cardForm.shouldSaveCard && patientId != null

    from(cardForm.createTilledPaymentMethod(billing))
      .pipe(
        switchMap(({ id }) => shouldAttach
          ? this.paymentService.attachTilledPaymentMethodToCustomer(id, patientId!).pipe(map((r) => r.id))
          : of(id)),
        switchMap((paymentMethodId) => this.api.applyPayment(balanceId, {
          method: TransactionMethod.STAFF_CREDIT_CARD,
          amount: value.amount!,
          provider: PAYMENT_PROVIDER.TILLED,
          paymentMethodId,
          paymentMethodTypes: ['card'],
          holderName: value.holderName || undefined,
          notes: value.notes || undefined,
          areNotesVisibleToPatient: value.areNotesVisibleToPatient ?? false,
          customFields: this.normalizeCustomFields(value.customFields),
          patientEmail: value.email || undefined,
        })),
        catchError((err) => {
          this.submitting.set(false)
          this.error.set(this.extractErrorMessage(err))
          return of(null)
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((applyResult) => {
        if (applyResult == null) {
          return
        }
        this.submitting.set(false)
        this.handleSuccess({
          balance: applyResult.balance,
          applied: applyResult.applied,
          reason: applyResult.reason,
        })
      })
  }

  private handleSuccess(payload: CollectPaymentResult): void {
    this.paymentApplied.emit(payload)
    if (payload.applied) {
      this.resetAfterSuccess(payload.balance)
    }
  }

  private resetAfterSuccess(refreshedBalance: EstimateBalance): void {
    const nextOutstanding = balanceOutstanding(refreshedBalance)
    const currentMethod = this.form.controls.paymentMethod.value
    this.form.reset({
      paymentMethod: currentMethod,
      amount: nextOutstanding,
      paymentMethodId: '',
      holderName: '',
      email: '',
      holderAddress: null,
      checkNumber: '',
      notes: '',
      areNotesVisibleToPatient: false,
      applyDiscount: false,
      customFields: null,
    })
    // Re-prime billing details for the next collection on the same patient.
    this.autofillFromPatient()
  }

  /** Strip the customFields envelope so a payload with all-null entries doesn't make it onto the wire. */
  private normalizeCustomFields(value: Record<string, unknown> | null | undefined): Record<string, unknown> | undefined {
    if (!value) {
      return undefined
    }
    const entries = Object.entries(value).filter(([, v]) => v !== null && v !== undefined && v !== '')
    return entries.length === 0 ? undefined : Object.fromEntries(entries)
  }

  private extractErrorMessage(err: unknown): string {
    if (err && typeof err === 'object') {
      const e = err as { error?: { message?: string }, message?: string }
      return e.error?.message ?? e.message ?? 'Payment failed'
    }
    return 'Payment failed'
  }

  // Currency formatter used in operator-facing prompt interpolation. Avoids
  // pulling in CurrencyPipe just for a confirm dialog.
  private formatUsd(value: number): string {
    return `$${value.toFixed(2)}`
  }

  private crossFieldValidator(): ValidationErrors | null {
    const { paymentMethod, paymentMethodId, holderName, email, holderAddress, checkNumber } = this.form?.value ?? {}
    if (paymentMethod === 'creditCard' && !paymentMethodId) {
      return { paymentMethodIdRequired: true }
    }
    if (paymentMethod === 'creditCard' && paymentMethodId === NEW_CARD_SENTINEL) {
      if (!holderName) {
        return { holderNameRequired: true }
      }
      // Tilled requires billing email + a country/zip pair to attach the method.
      if (this.isTilledProvider) {
        if (!email) {
          return { emailRequired: true }
        }
        if (!holderAddress?.country || !holderAddress?.zip) {
          return { holderAddressRequired: true }
        }
      }
    }
    if (paymentMethod === 'check' && !checkNumber) {
      return { checkNumberRequired: true }
    }
    return null
  }
}
